import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  FileText,
  Hourglass,
  ShieldCheck,
} from 'lucide-react';
import { reportsService } from '../../services/reports';
import { DateRangePicker } from '../../components/reports/DateRangePicker';
import { TimelineChart } from '../../components/reports/TimelineChart';
import { PieChart } from '../../components/reports/PieChart';
import { DataTable } from '../../components/reports/DataTable';
import { PageHeader, KpiCard, EmptyState } from '@/components/ui';

// Match the StatusBadge palette so the chart reads the same as badges elsewhere.
const STATUS_CHART_COLORS: Record<string, string> = {
  DRAFT: '#94a3b8',
  ACTIVE: '#28a091',
  PARTIALLY_FULFILLED: '#f97316',
  FULFILLED: '#22c55e',
  CANCELLED: '#ef4444',
  EXPIRED: '#f59e0b',
};

const severityStyles: Record<string, { box: string; text: string }> = {
  SEVERE: { box: 'border-danger-200 bg-danger-50', text: 'text-danger-700' },
  MODERATE: { box: 'border-warning-200 bg-warning-50', text: 'text-warning-800' },
  MILD: { box: 'border-primary-200 bg-primary-50', text: 'text-primary-700' },
};

export function DoctorReports() {
  const { t, i18n } = useTranslation();
  const numberLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const [dateRange, setDateRange] = useState({
    fromDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    toDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const { data: overview } = useQuery({
    queryKey: ['doctor-overview', dateRange],
    queryFn: () => reportsService.getDoctorOverview(dateRange),
  });

  const { data: timeline } = useQuery({
    queryKey: ['doctor-timeline', dateRange, groupBy],
    queryFn: () => reportsService.getDoctorTimeline({ ...dateRange, groupBy }),
  });

  const { data: statusBreakdown } = useQuery({
    queryKey: ['doctor-status-breakdown', dateRange],
    queryFn: () => reportsService.getDoctorStatusBreakdown(dateRange),
  });

  const { data: topDrugs } = useQuery({
    queryKey: ['doctor-top-drugs', dateRange],
    queryFn: () => reportsService.getDoctorTopDrugs({ ...dateRange, limit: 10 }),
  });

  const { data: interactions } = useQuery({
    queryKey: ['doctor-interactions', dateRange],
    queryFn: () => reportsService.getDoctorInteractions(dateRange),
  });

  const handleDateChange = (fromDate: string, toDate: string) => {
    setDateRange({ fromDate, toDate });
  };

  const groupByLabels: Record<'day' | 'week' | 'month', string> = {
    day: t('reports.day'),
    week: t('reports.week'),
    month: t('reports.month'),
  };

  const fmt = (n?: number | null) => (n ?? 0).toLocaleString(numberLocale);

  return (
    <div className="space-y-6">
      <PageHeader title={t('reports.title')} description={t('doctor.reports.description')} />

      <DateRangePicker
        fromDate={dateRange.fromDate}
        toDate={dateRange.toDate}
        onChange={handleDateChange}
      />

      {/* Overview Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <KpiCard
          label={t('doctor.reports.totalPrescriptions')}
          value={fmt(overview?.totalPrescriptions)}
          icon={FileText}
          tone="primary"
        />
        <KpiCard
          label={t('prescription.status.fulfilled')}
          value={fmt(overview?.fulfilledPrescriptions)}
          icon={CheckCircle2}
          tone="success"
        />
        <KpiCard
          label={t('prescription.status.partially_fulfilled')}
          value={fmt(overview?.partiallyFulfilledPrescriptions)}
          icon={Hourglass}
          tone="warning"
        />
        <KpiCard
          label={t('prescription.status.active')}
          value={fmt(overview?.activePrescriptions)}
          icon={Clock}
          tone="navy"
        />
        <KpiCard
          label={t('prescription.status.expired')}
          value={fmt(overview?.expiredPrescriptions)}
          icon={AlertTriangle}
          tone="gray"
        />
        <KpiCard
          label={t('prescription.status.cancelled')}
          value={fmt(overview?.cancelledPrescriptions)}
          icon={Ban}
          tone="danger"
        />
      </div>

      {/* Timeline and Status Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <TimelineChart
            data={timeline || []}
            groupBy={groupBy}
            title={t('doctor.reports.prescriptionsOverTime')}
            actions={
              <div
                className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm"
                role="group"
                aria-label={t('reports.groupBy')}
              >
                {(['day', 'week', 'month'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={groupBy === option}
                    onClick={() => setGroupBy(option)}
                    className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                      groupBy === option
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {groupByLabels[option]}
                  </button>
                ))}
              </div>
            }
          />
        </div>
        <div className="min-w-0">
          <PieChart
            data={
              statusBreakdown?.map((s) => ({
                name: t(`prescription.status.${s.status.toLowerCase()}`),
                value: s.count,
                color: STATUS_CHART_COLORS[s.status],
              })) || []
            }
            title={t('doctor.reports.statusDistribution')}
          />
        </div>
      </div>

      {/* Top Drugs and Interactions */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <DataTable
            title={t('doctor.reports.mostPrescribedDrugs')}
            data={topDrugs || []}
            emptyMessage={t('doctor.reports.noData')}
            columns={[
              { key: 'tradeName', header: t('prescription.drug') },
              { key: 'dci', header: t('doctor.reports.dci') },
              {
                key: 'totalQuantity',
                header: t('reports.totalQty'),
                render: (row) => fmt(row.totalQuantity),
              },
              {
                key: 'prescriptionCount',
                header: t('reports.rxCount'),
                render: (row) => fmt(row.prescriptionCount),
              },
            ]}
            maxRows={10}
          />
        </div>

        <div className="card min-w-0 p-0">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="card-title">{t('doctor.reports.interactionWarnings')}</h3>
          </div>
          {!interactions || interactions.length === 0 ? (
            <EmptyState icon={ShieldCheck} title={t('doctor.reports.noInteractions')} />
          ) : (
            <div className="space-y-3 p-5">
              {interactions.map((interaction) => {
                const style = severityStyles[interaction.severity] ?? severityStyles.MILD!;
                return (
                  <div key={interaction.severity} className={`rounded-lg border p-4 ${style.box}`}>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className={`font-semibold ${style.text}`}>
                        {t(
                          `interactions.${interaction.severity.toLowerCase()}`,
                          interaction.severity
                        )}
                      </span>
                      <span className="text-sm text-gray-600">
                        {t('doctor.reports.occurrences', { n: interaction.count })}
                      </span>
                    </div>
                    <ul className="space-y-0.5 text-sm text-gray-700">
                      {(interaction.drugs ?? []).slice(0, 3).map((d, i) => (
                        <li key={i} className="break-words">
                          {d.drug1} + {d.drug2}
                        </li>
                      ))}
                      {(interaction.drugs?.length ?? 0) > 3 && (
                        <li className="text-gray-500">
                          {t('doctor.reports.andMore', { n: (interaction.drugs?.length ?? 0) - 3 })}
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
